import { Outlet, useRouterState } from '@tanstack/react-router'
import { ToastProvider } from '@/lib/messaging'
import { CuidariToastProvider } from '@/lib/mui/toast'

/**
 * Routes that bypass `useOnboardingRedirect` because they predate auth
 * (login, password reset confirmation) or are part of the onboarding
 * hand-off itself (entrar).
 *
 * T9 F3.5 audit: `/bills` was historically in this set (added by the
 * apelles porting commit `aec18f6`) but BillsPage requires an authed
 * session (wraps `<AppShell>` which depends on Cerberus claims). The
 * deep-link landing path uses `return_to` post-login, so making `/bills`
 * itself public is a bug — removed. Cerberus magic-link redemption
 * happens through `/entrar` exclusively.
 *
 * The public `/signup` route was deleted alongside the addition of `/reset`
 * (waitlist-gate-and-reset-flow): the only legitimate path for creating
 * a Cerberus account in a waitlist-gated product is `/entrar?invite=…`.
 * Allowing anonymous `/signup` bypassed the waitlist entirely.
 */
export const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  '/login',
  '/reset',
  '/entrar',
  '/verify',
  // Phase 8 (M8) mobile-web-minimal public surfaces.
  // - `/` is dual-mode: the route is registered as public so unauthed
  //   visitors hit the install gate (`HomePage` renders
  //   `<InstallCTA webContinueHref="/login" />`, which promotes the native
  //   app with a discreet web-continue link) instead of being bounced to
  //   /login. Authed visitors get the dashboard via `HomePage`'s internal
  //   auth check. (The old marketing `LandingPublic` was retired.)
  // - `/instalar` is the install CTA page.
  // - `/share/*` carries a token-only public preview of a safe share
  //   card; never authed, never PII beyond what the token encodes.
  '/',
  '/instalar',
])

/** Phase 8 (M8) — prefix-based public paths (e.g. `/share/<token>`). */
const PUBLIC_PREFIXES: readonly string[] = ['/share/']

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.has(pathname)) return true
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))
}

export function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  // ToastProvider lives INSIDE the router context (M0 hotfix 2026-05-25):
  // the Pheme sanitizer added in PR #260 now emits drop envelopes via
  // useTrack, which reads useRouterState. When ToastProvider was mounted
  // as a sibling of <RouterProvider> in main.tsx, that hook chain panicked
  // on first mount and the whole SPA failed to paint at /entrar (waitlist
  // gate). Moving the provider here keeps it portal-rendered above the
  // route tree but inside the router context, so analytics never has a
  // null state to read.
  if (isPublicPath(pathname)) return <CuidariToastProvider><Outlet /><ToastProvider /></CuidariToastProvider>
  // Home e qualquer outra rota renderizam direto (AppShell vem do HomePage).
  return <CuidariToastProvider><Outlet /><ToastProvider /></CuidariToastProvider>
}
